/**
 * ExecutiveCardWidget - Configurable widget for displaying selected executive metrics
 * Admin can select which metrics to show when adding to any user's dashboard
 */

import { useState, useEffect, useCallback } from 'react';
import { Card } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Label } from '../../ui/label';
import { Skeleton } from '../../ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../ui/dialog';
import { cn } from '../../../lib/utils';
import {
  DollarSign,
  Shield,
  Activity,
  Users,
  Cloud,
  Server,
  AlertTriangle,
  CheckCircle2,
  TrendingUp,
  FolderKanban,
  Phone,
  FileSpreadsheet,
  Loader2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Available metrics that can be toggled
const AVAILABLE_METRICS = [
  { 
    id: 'monthly_costs', 
    label: 'Monthly Cost Tracking', 
    description: 'Total monthly IT costs (Azure + Licensing)',
    icon: DollarSign,
    category: 'costs'
  },
  { 
    id: 'azure_costs', 
    label: 'Azure Costs', 
    description: 'Azure cloud spending breakdown',
    icon: Cloud,
    category: 'costs'
  },
  { 
    id: 'm365_licensing', 
    label: 'M365 Licensing Costs', 
    description: 'Microsoft 365 license spending',
    icon: DollarSign,
    category: 'costs'
  },
  { 
    id: 'smartsheet_costs', 
    label: 'Smartsheet Costs', 
    description: 'Smartsheet subscription costs',
    icon: FileSpreadsheet,
    category: 'costs'
  },
  { 
    id: 'service_health', 
    label: 'Service Health', 
    description: 'M365 service health status',
    icon: Activity,
    category: 'operations'
  },
  { 
    id: 'internal_services', 
    label: 'Internal Services', 
    description: 'Status of internal monitored services',
    icon: Server,
    category: 'operations'
  },
  { 
    id: 'security_score', 
    label: 'Security Score', 
    description: 'Microsoft Secure Score',
    icon: Shield,
    category: 'security'
  },
  { 
    id: 'active_incidents', 
    label: 'Active Incidents', 
    description: 'Current open incidents count',
    icon: AlertTriangle,
    category: 'operations'
  },
  { 
    id: 'total_users', 
    label: 'Total Users', 
    description: 'Total licensed users count',
    icon: Users,
    category: 'identity'
  },
  { 
    id: 'ringcentral', 
    label: 'RingCentral Info', 
    description: 'Phone system status',
    icon: Phone,
    category: 'services'
  },
  { 
    id: 'projects_summary', 
    label: 'Projects Overview', 
    description: 'Active vs Total projects count',
    icon: FolderKanban,
    category: 'projects'
  },
];

// Default selected metrics
const DEFAULT_METRICS = ['monthly_costs', 'service_health', 'security_score', 'total_users'];

// Format currency
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Configuration Dialog Component
export const ExecutiveCardConfigDialog = ({ open, onOpenChange, config, onSave }) => {
  const [selectedMetrics, setSelectedMetrics] = useState(config?.selectedMetrics || DEFAULT_METRICS);

  useEffect(() => {
    if (open) {
      setSelectedMetrics(config?.selectedMetrics || DEFAULT_METRICS);
    }
  }, [open, config]);

  const toggleMetric = (metricId) => {
    setSelectedMetrics(prev => 
      prev.includes(metricId) 
        ? prev.filter(id => id !== metricId)
        : [...prev, metricId]
    );
  };

  const handleSave = () => {
    onSave({ selectedMetrics });
    onOpenChange(false);
  };

  // Group metrics by category
  const groupedMetrics = AVAILABLE_METRICS.reduce((acc, metric) => {
    if (!acc[metric.category]) acc[metric.category] = [];
    acc[metric.category].push(metric);
    return acc;
  }, {});

  console.log('[ExecutiveCardConfig] Available metrics:', AVAILABLE_METRICS.map(m => m.id));
  console.log('[ExecutiveCardConfig] Grouped metrics:', Object.keys(groupedMetrics));
  console.log('[ExecutiveCardConfig] Operations metrics:', groupedMetrics.operations?.map(m => m.id));

  const categoryLabels = {
    costs: 'Cost Tracking',
    operations: 'Operations',
    security: 'Security',
    identity: 'Identity',
    services: 'External Services',
    projects: 'Projects',
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Configure Executive Card</DialogTitle>
          <DialogDescription>
            Select which metrics to display in this widget
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4 max-h-[60vh] overflow-y-auto">
          {Object.entries(groupedMetrics).map(([category, metrics]) => (
            <div key={category} className="space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                {categoryLabels[category] || category}
              </h4>
              <div className="space-y-2">
                {metrics.map((metric) => {
                  const Icon = metric.icon;
                  const isSelected = selectedMetrics.includes(metric.id);
                  return (
                    <div
                      key={metric.id}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                        isSelected 
                          ? "border-primary bg-primary/5" 
                          : "border-border hover:border-primary/50"
                      )}
                      onClick={() => toggleMetric(metric.id)}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleMetric(metric.id)}
                        className="pointer-events-none"
                      />
                      <div className="p-1.5 rounded-lg bg-primary/10">
                        <Icon className="w-4 h-4 text-primary" />
                      </div>
                      <div className="flex-1">
                        <Label className="text-sm font-medium cursor-pointer">
                          {metric.label}
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {metric.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={selectedMetrics.length === 0}>
            Save Configuration
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Helper function for fetch with timeout
const fetchWithTimeout = async (url, options = {}, timeoutMs = 10000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(timeoutId);
    return response;
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      throw new Error('Request timed out');
    }
    throw err;
  }
};

// Main Widget Component
export const ExecutiveCardWidget = ({ config, onConfigure }) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const selectedMetrics = config?.selectedMetrics || DEFAULT_METRICS;

  // Get Azure Management token for cost API
  const getAzureManagementToken = useCallback(async () => {
    try {
      const { PublicClientApplication } = await import('@azure/msal-browser');
      const { msalConfig, azureManagementRequest } = await import('../../../config/authConfig');
      
      const msalInstance = new PublicClientApplication(msalConfig);
      await msalInstance.initialize();
      
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        console.warn('[ExecutiveCard] No MSAL accounts found for Azure token');
        return null;
      }
      
      try {
        const response = await msalInstance.acquireTokenSilent({
          ...azureManagementRequest,
          account: accounts[0],
        });
        return response.accessToken;
      } catch (silentError) {
        console.warn('[ExecutiveCard] Silent Azure token acquisition failed:', silentError);
        return null;
      }
    } catch (err) {
      console.error('[ExecutiveCard] Failed to get Azure Management token:', err);
      return null;
    }
  }, []);

  // Fetch all data needed for selected metrics
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const results = {};

      // Get Azure token for cost APIs
      let azureToken = null;
      if (selectedMetrics.includes('azure_costs') || selectedMetrics.includes('monthly_costs')) {
        azureToken = await getAzureManagementToken();
      }

      // Fetch dashboard summary (covers most metrics) - with timeout
      try {
        const summaryRes = await fetchWithTimeout(`${API_URL}/api/dashboard/summary`);
        if (summaryRes.ok) {
          const summary = await summaryRes.json();
          results.summary = summary;
        }
      } catch (e) {
        console.warn('Failed to fetch summary:', e.message);
      }

      // Fetch Azure costs if needed - with timeout and Azure token
      if (selectedMetrics.includes('azure_costs') || selectedMetrics.includes('monthly_costs')) {
        try {
          const headers = { 'Content-Type': 'application/json' };
          if (azureToken) {
            headers['Authorization'] = `Bearer ${azureToken}`;
            console.log('[ExecutiveCard] Using Azure token for cost API');
          } else {
            console.log('[ExecutiveCard] No Azure token available, checking for cached data');
          }
          
          const azureRes = await fetchWithTimeout(
            `${API_URL}/api/azure/costs/all-scopes?time_range_days=30`,
            { headers }
          );
          if (azureRes.ok) {
            const azureData = await azureRes.json();
            results.azureCosts = azureData.total || 0;
            console.log('[ExecutiveCard] Azure costs fetched:', results.azureCosts, 'data source:', azureData.data_source);
            
            // Cache successful result for other widgets to use
            if (results.azureCosts > 0) {
              try {
                sessionStorage.setItem('cachedAzureCosts', JSON.stringify({
                  total: results.azureCosts,
                  timestamp: Date.now()
                }));
              } catch (e) { /* ignore storage errors */ }
            }
          } else {
            console.warn('[ExecutiveCard] Azure costs API returned:', azureRes.status);
          }
          
          // If we didn't get data, try to use cached value (from main dashboard)
          if (!results.azureCosts || results.azureCosts === 0) {
            try {
              const cached = sessionStorage.getItem('cachedAzureCosts');
              if (cached) {
                const cachedData = JSON.parse(cached);
                // Use cache if it's less than 1 hour old
                if (Date.now() - cachedData.timestamp < 3600000) {
                  results.azureCosts = cachedData.total;
                  console.log('[ExecutiveCard] Using cached Azure costs:', results.azureCosts);
                }
              }
            } catch (e) { /* ignore storage errors */ }
          }
        } catch (e) {
          console.warn('[ExecutiveCard] Failed to fetch Azure costs:', e.message);
          
          // Try cached value on error
          try {
            const cached = sessionStorage.getItem('cachedAzureCosts');
            if (cached) {
              const cachedData = JSON.parse(cached);
              if (Date.now() - cachedData.timestamp < 3600000) {
                results.azureCosts = cachedData.total;
                console.log('[ExecutiveCard] Using cached Azure costs after error:', results.azureCosts);
              }
            }
          } catch (cacheErr) { /* ignore */ }
          
          if (!results.azureCosts) {
            results.azureCosts = 0;
          }
        }
      }

      // Fetch service health if needed - with timeout
      if (selectedMetrics.includes('service_health')) {
        try {
          const healthRes = await fetchWithTimeout(`${API_URL}/api/services/health`);
          if (healthRes.ok) {
            const healthData = await healthRes.json();
            results.serviceHealth = healthData;
          }
        } catch (e) {
          results.serviceHealth = null;
        }
      }

      // Fetch external services if needed - with timeout
      if (selectedMetrics.includes('ringcentral') || selectedMetrics.includes('smartsheet_costs')) {
        try {
          const extRes = await fetchWithTimeout(`${API_URL}/api/external-services`);
          if (extRes.ok) {
            const extData = await extRes.json();
            results.externalServices = extData;
          }
        } catch (e) {
          results.externalServices = null;
        }
      }

      // Fetch projects if needed - with timeout
      if (selectedMetrics.includes('projects_summary')) {
        try {
          const projRes = await fetchWithTimeout(`${API_URL}/api/projects`);
          if (projRes.ok) {
            const projData = await projRes.json();
            // API returns {projects: [], count: N}
            results.projects = projData.projects || projData || [];
          }
        } catch (e) {
          results.projects = [];
        }
      }

      // Fetch internal services if needed - with timeout
      if (selectedMetrics.includes('internal_services')) {
        try {
          const servicesRes = await fetchWithTimeout(`${API_URL}/api/services/internal`);
          if (servicesRes.ok) {
            const servicesData = await servicesRes.json();
            results.internalServices = servicesData;
          }
        } catch (e) {
          results.internalServices = [];
        }
      }

      setData(results);
    } catch (err) {
      console.error('Failed to fetch executive data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [selectedMetrics, getAzureManagementToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Render a single metric card
  const renderMetric = (metricId) => {
    const metricConfig = AVAILABLE_METRICS.find(m => m.id === metricId);
    if (!metricConfig) return null;

    const Icon = metricConfig.icon;
    let value = null;
    let subValue = null;
    let status = 'neutral';

    switch (metricId) {
      case 'monthly_costs': {
        const azureCost = data.azureCosts || 0;
        const licensingCost = data.summary?.monthly_spend?.licensing || 0;
        const smartsheetCost = data.summary?.monthly_spend?.smartsheet || 0;
        const otherCost = data.summary?.monthly_spend?.other || 0;
        value = formatCurrency(azureCost + licensingCost + smartsheetCost + otherCost);
        subValue = `Azure: ${formatCurrency(azureCost)} | Licensing: ${formatCurrency(licensingCost)}`;
        break;
      }
      case 'azure_costs': {
        value = formatCurrency(data.azureCosts || 0);
        subValue = 'Month to date';
        break;
      }
      case 'm365_licensing': {
        value = formatCurrency(data.summary?.monthly_spend?.licensing || 0);
        subValue = `${data.summary?.total_licenses || 0} total licenses`;
        break;
      }
      case 'smartsheet_costs': {
        value = formatCurrency(data.summary?.monthly_spend?.smartsheet || 0);
        const smartsheet = data.externalServices?.smartsheet;
        subValue = smartsheet ? `${smartsheet.licensed_users || 0} users` : 'Smartsheet';
        break;
      }
      case 'service_health': {
        const services = data.serviceHealth?.services || [];
        const healthy = services.filter(s => s.status === 'serviceDegradation' || s.status === 'serviceOperational').length;
        const degraded = services.filter(s => s.status !== 'serviceOperational').length;
        value = `${healthy}/${services.length}`;
        subValue = degraded > 0 ? `${degraded} degraded` : 'All operational';
        status = degraded > 0 ? 'warning' : 'success';
        break;
      }
      case 'security_score': {
        const score = data.summary?.security?.secure_score;
        value = score ? `${Math.round(score)}%` : 'N/A';
        subValue = 'Microsoft Secure Score';
        status = score > 70 ? 'success' : score > 50 ? 'warning' : 'danger';
        break;
      }
      case 'active_incidents': {
        const incidents = data.summary?.operations?.active_incidents || 0;
        value = incidents.toString();
        subValue = incidents === 0 ? 'No active incidents' : 'Requires attention';
        status = incidents === 0 ? 'success' : 'warning';
        break;
      }
      case 'total_users': {
        value = (data.summary?.total_users || 0).toLocaleString();
        subValue = 'Licensed users';
        break;
      }
      case 'ringcentral': {
        const rc = data.externalServices?.ringcentral;
        value = rc?.total_users?.toString() || 'N/A';
        subValue = rc ? 'RingCentral users' : 'Not connected';
        break;
      }
      case 'projects_summary': {
        const projects = Array.isArray(data.projects) ? data.projects : [];
        const active = projects.filter(p => p.status === 'active' || p.status === 'in_progress').length;
        const total = projects.length;
        value = `${active}/${total}`;
        subValue = `${active} active of ${total} total`;
        status = active > 0 ? 'success' : 'neutral';
        break;
      }
      case 'internal_services': {
        const services = Array.isArray(data.internalServices) ? data.internalServices : [];
        // Status can be 'up', 'down', 'online', 'offline'
        const online = services.filter(s => 
          s.status === 'up' || s.status === 'online' || s.is_healthy === true
        ).length;
        const total = services.length;
        value = `${online}/${total}`;
        subValue = online === total ? 'All services online' : `${total - online} offline`;
        status = online === total ? 'success' : online > 0 ? 'warning' : 'danger';
        break;
      }
      default:
        value = 'N/A';
    }

    const statusColors = {
      success: 'text-emerald-500',
      warning: 'text-amber-500',
      danger: 'text-red-500',
      neutral: 'text-primary',
    };

    return (
      <div 
        key={metricId}
        className="p-3 rounded-lg bg-background/50 border border-border/30 hover:border-primary/30 transition-colors"
      >
        <div className="flex items-start gap-3">
          <div className={cn("p-2 rounded-lg", 
            status === 'success' ? 'bg-emerald-500/10' :
            status === 'warning' ? 'bg-amber-500/10' :
            status === 'danger' ? 'bg-red-500/10' :
            'bg-primary/10'
          )}>
            <Icon className={cn("w-4 h-4", statusColors[status])} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground mb-0.5">{metricConfig.label}</p>
            <p className={cn("text-xl font-bold", statusColors[status])} style={{ fontFamily: 'Chivo, sans-serif' }}>
              {value}
            </p>
            {subValue && (
              <p className="text-xs text-muted-foreground truncate">{subValue}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center text-destructive">
        <p>{error}</p>
      </div>
    );
  }

  if (selectedMetrics.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-muted-foreground gap-2">
        <p>No metrics selected</p>
        <Button variant="outline" size="sm" onClick={() => onConfigure?.()}>
          Configure Widget
        </Button>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto">
      <div className="grid grid-cols-2 gap-2">
        {selectedMetrics.map(metricId => renderMetric(metricId))}
      </div>
    </div>
  );
};

export default ExecutiveCardWidget;
